// schema.org JSON-LD builders shared by the pages (via Helmet) and the
// build-time prerender script. Keep this file free of React and path aliases.
import { Locale, LOCALE_META, localizePath, SITE_URL } from "../i18n/config";

type Row = Record<string, unknown>;
const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const absolute = (url?: string) => (url ? (url.startsWith("http") ? url : `${SITE_URL}${url}`) : undefined);
const compact = <T extends Row>(o: T) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== null && !(Array.isArray(v) && !v.length))) as T;

const uniqueExcept = (primary: string, values: (string | undefined)[]) =>
  [...new Set(values.filter((v): v is string => !!v && v !== primary))];

const PUBLISHER = { "@type": "Organization", name: "BioNexus", url: SITE_URL };

/** Source publication of an entry as a citation (DOI when available). */
const citationOf = (row: Row) => {
  const doi = str(row.source_doi);
  const title = str(row.source_paper);
  const url = str(row.source_url);
  if (!doi && !title && !url) return undefined;
  return compact({
    "@type": doi || title ? "ScholarlyArticle" : "CreativeWork",
    name: title,
    identifier: doi ? `https://doi.org/${doi}` : undefined,
    url: doi ? `https://doi.org/${doi}` : url,
    sameAs: doi && url && !url.includes(doi) ? url : undefined,
  });
};

/** A material entry is a small curated dataset of measured properties with a cited source. */
export const materialJsonLd = (row: Row, locale: Locale, text: { name: string; description?: string | null }) => {
  const measured = [
    ["Biodegradation", str(row.degradation_value) ?? (num(row.degradation_days) ? `${row.degradation_days} days` : undefined)],
    ["Tensile strength (MPa)", num(row.tensile_strength_mpa)],
    ["Electrical performance", str(row.electrical_performance)],
    ["Water resistance", str(row.water_resistance)],
    ["Cost per kg (USD)", num(row.cost_per_kg)],
    ["Technology readiness level (BioNexus assessment)", num(row.trl_level)],
  ].filter(([, v]) => v !== undefined);

  return compact({
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: text.name,
    alternateName: uniqueExcept(text.name, [str(row.name), str(row.name_cn)]),
    description:
      str(text.description) ??
      `Curated entry for ${str(row.name)}${str(row.family) ? ` (${row.family})` : ""}: measured properties with their cited source.`,
    url: `${SITE_URL}${localizePath(`/materials/${row.id}`, locale)}`,
    inLanguage: LOCALE_META[locale].htmlLang,
    keywords: [str(row.family), str(row.primary_application), "bio-based materials", "biodegradable electronics"].filter(Boolean),
    image: absolute(str(row.image_url)),
    dateModified: str(row.updated_at),
    isAccessibleForFree: true,
    creator: PUBLISHER,
    publisher: PUBLISHER,
    citation: citationOf(row),
    variableMeasured: measured.map(([name, value]) => ({ "@type": "PropertyValue", name, value })),
  });
};

/** An application entry documents a project; it cites the project's own source. */
export const applicationJsonLd = (row: Row, locale: Locale, text: { title: string; description?: string | null }) =>
  compact({
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: text.title,
    alternateName: uniqueExcept(text.title, [str(row.title), str(row.title_cn)]),
    description: str(text.description),
    url: `${SITE_URL}${localizePath(`/applications/${row.id}`, locale)}`,
    inLanguage: LOCALE_META[locale].htmlLang,
    keywords: [str(row.category), ...(Array.isArray(row.tags) ? (row.tags as string[]) : [])].filter(Boolean),
    image: absolute(str(row.image_url)),
    dateCreated: num(row.year) ? String(row.year) : undefined,
    dateModified: str(row.updated_at),
    publisher: PUBLISHER,
    sourceOrganization: str(row.organization) ? { "@type": "Organization", name: str(row.organization) } : undefined,
    citation: str(row.source_url) ? { "@type": "CreativeWork", url: str(row.source_url) } : undefined,
  });

/** List pages: an ItemList pointing at each entry. */
export const itemListJsonLd = (locale: Locale, kind: "materials" | "applications", items: { id: string; name: string }[]) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: `${SITE_URL}${localizePath(`/${kind}/${it.id}`, locale)}`,
  })),
});
